/**
 * Bundle Options Accordion Functionality
 */
if (!customElements.get('bundle-options')) {
  // Import Shopify's PubSub system for events
  const PUB_SUB_EVENTS = {
    optionValueSelectionChange: 'option-value-selection-change',
  };

  customElements.define(
    'bundle-options',
    class BundleOptions extends HTMLElement {
      constructor() {
        super();
        this._processingClick = false;
        this._eventProcessCount = 0;
        this._lastEventTime = 0;
        this.setupEventListeners();
      }

      setupEventListeners() {
        const headers = this.querySelectorAll('.bundle-option__header');

        headers.forEach((header) => {
          header.addEventListener('click', (e) => {
            // Prevent recursive handling
            if (this._processingClick) {
              //console.log('Already processing a bundle click, preventing recursive call');
              return;
            }
            
            // Set guard flag
            this._processingClick = true;
            
            try {
              // Get the radio input
              const bundleOption = header.closest('.bundle-option');
              const input = bundleOption.querySelector('input[type="radio"]');

              if (input.disabled) {
                this._processingClick = false;
                return;
              }

              // Only check the radio if it's not already checked
              const wasChecked = input.checked;
              if (!wasChecked) {
                //console.log(`Checking bundle radio: ${input.value}`);
                input.checked = true;
              } else {
                //console.log(`Bundle radio already checked: ${input.value}`);
              }

              // Find out which option this is (get the option position from data-attributes)
              const optionPosition = input.dataset.optionPosition || '2';
              //console.log(`Bundle option position: ${optionPosition}`);

              // Get the parent variant-selects
              const variantSelects = this.closest('variant-selects');

              // Find the no-ajax-variants component (if it exists)
              const noAjaxVariants = document.querySelector('no-ajax-variants');
              const productInfo = document.querySelector('product-info[data-update-url="false"]');

              // If we're using no-ajax-variants, trigger its handler directly
              if (noAjaxVariants && productInfo && typeof noAjaxVariants.handleBundleOptionChange === 'function') {
                //console.log('Bundle option change - using no-ajax-variants');
                
                // Only dispatch change event if the radio wasn't already checked
                if (!wasChecked) {
                  // Create and dispatch the change event for DOM consistency
                  const changeEvent = new Event('change', { bubbles: true });
                  // Mark event to prevent loops
                  changeEvent._generatedByBundleOptions = true;
                  changeEvent._bundleOptionValue = input.value;
                  changeEvent._bundleOptionPosition = optionPosition;
                  
                  input.dispatchEvent(changeEvent);
                  //console.log('Dispatched change event on bundle radio');
                } else {
                  //console.log('Skipping change event for already-checked bundle radio');
                }
                
                // Let the no-ajax-variants component handle it
                setTimeout(() => {
                  try {
                    noAjaxVariants.handleBundleOptionChange();
                    //console.log('Bundle option change handled by no-ajax-variants');
                  } catch (e) {
                    //console.error('Error in handleBundleOptionChange:', e);
                  } finally {
                    // Always clear flag
                    this._processingClick = false;
                  }
                }, 10);
              } else if (variantSelects) {
                // Standard flow - use variant-selects
                // Only dispatch change event if the radio wasn't already checked
                if (!wasChecked) {
                  // Create a change event that will bubble up to the variant-selects element
                  const changeEvent = new Event('change', { bubbles: true });
                  // Mark event to prevent loops
                  changeEvent._generatedByBundleOptions = true;
                  changeEvent._bundleOptionValue = input.value;
                  changeEvent._bundleOptionPosition = optionPosition;
                  
                  input.dispatchEvent(changeEvent);
                  //console.log('Dispatched change event for variant-selects');

                  // If we still need to force it, try publishing the event directly
                  try {
                    if (typeof window.publish === 'function') {
                      const selectedOptionElements = Array.from(
                        variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
                      );
                      
                      //console.log('Publishing with selected option values:', selectedOptionElements.length);
                      
                      window.publish('option-value-selection-change', {
                        data: {
                          event: changeEvent,
                          target: input,
                          // This must match the structure in VariantSelects.selectedOptionValues
                          selectedOptionValues: selectedOptionElements.map(({ dataset }) => dataset.optionValueId),
                        },
                      });
                      //console.log('Published option value selection change event');
                    }
                  } catch (e) {
                    //console.error('Error publishing event:', e);
                  }
                } else {
                  //console.log('Skipping change event for already-checked bundle radio');
                }
                
                // Clear flag
                this._processingClick = false;
              } else {
                // Fallback if we can't find the parent
                if (!wasChecked) {
                  const changeEvent = new Event('change', { bubbles: true });
                  // Mark event to prevent loops
                  changeEvent._generatedByBundleOptions = true;
                  changeEvent._bundleOptionValue = input.value;
                  
                  input.dispatchEvent(changeEvent);
                  //console.log('Dispatched change event (fallback)');
                } else {
                  //console.log('Skipping change event for already-checked bundle radio (fallback)');
                }
                
                // Clear flag
                this._processingClick = false;
              }

              // Update UI immediately for better UX
              this.updateUI();
            } catch (e) {
              //console.error('Error in bundle-options click handler:', e);
              this._processingClick = false;
            }
          });
        });

        // Also listen for changes from Shopify's variant selectors
        document.addEventListener('variant:changed', () => {
          this.updateUI();
        });
        
        // Listen for our own events
        this.addEventListener('change', (e) => {
          // Check for a special flag to prevent loops
          if (this._updatingFromFormInputFix) {
            //console.log('Ignoring change events during form-input-fix update');
            return;
          }
          
          // Detect rapid event firing which might indicate a loop
          const now = Date.now();
          if (now - this._lastEventTime < 200) {
            this._eventProcessCount++;
            if (this._eventProcessCount > 3) {
              //console.warn('Too many events in short period, possible loop detected');
              e.stopPropagation();
              return;
            }
          } else {
            this._eventProcessCount = 1;
          }
          this._lastEventTime = now;
          
          // Check if this is a generated event that we should ignore
          if (e.target.type === 'radio' && (e._generatedByNoAjaxVariants || e._generatedByFormInputFix || e._generatedByBundleOptions)) {
            //console.log('Ignoring generated change event on bundle radio');
            e.stopPropagation();
            return;
          }
          
          // Otherwise, update UI for real user events
          if (e.target.type === 'radio') {
            //console.log('Bundle radio changed via event, updating UI');
            this.updateUI();
          }
        }, true); // Use capture phase to stop events as early as possible
      }

      updateUI() {
        if (this._updatingFromFormInputFix) {
          //console.log('UpdateUI: Skipping event generation during form-input-fix update');
        }
        
        // Find the checked input
        const checkedInput = this.querySelector('input[type="radio"]:checked');

        if (!checkedInput) return;

        const selectedBundle = checkedInput.closest('.bundle-option');

        // Update all bundle options
        this.querySelectorAll('.bundle-option').forEach((option) => {
          const isActive = option === selectedBundle;

          // Update active class
          option.classList.toggle('bundle-option--active', isActive);

          // Update the checkmark
          const checkMarkDiv = option.querySelector('.CheckMarkDiv');
          if (checkMarkDiv) {
            if (isActive) {
              // Make it an active checkmark
              checkMarkDiv.classList.remove('t-border', 't-border-current');
              checkMarkDiv.classList.add('t-bg-Main');
              
              // Add SVG if it doesn't exist
              if (!checkMarkDiv.querySelector('svg')) {
                checkMarkDiv.innerHTML = `
                  <svg
                    width="18"
                    height="18"
                    class="t-w-3 t-h-3 t-text-White"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M2 6L5 9L10 3"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                `;
              }
            } else {
              // Make it an empty box
              checkMarkDiv.classList.add('t-border', 't-border-current');
              checkMarkDiv.classList.remove('t-bg-Main');
              
              // Remove SVG if it exists
              if (checkMarkDiv.querySelector('svg')) {
                checkMarkDiv.innerHTML = '';
              }
            }
          }

          // Update accordion visibility
          const content = option.querySelector('.bundle-option__content');
          //const toggle = option.querySelector('.bundle-option__toggle');
          
          if (isActive) {
            content.classList.remove('t-max-h-0');
            content.classList.add('t-max-h-96');
            //toggle.classList.add('t-rotate-180');
          } else {
            content.classList.add('t-max-h-0');
            content.classList.remove('t-max-h-96');
            //toggle.classList.remove('t-rotate-180');
          }
        });

        // Update selected value display
        const selectedValue = checkedInput.value;
        const displayElement = this.closest('.bundle-options-wrapper').querySelector('[data-selected-value]');
        if (displayElement) {
          displayElement.textContent = selectedValue;
        }
      }
    },
  );
}
